import React, {Dispatch, SetStateAction, useEffect, useState} from "react";
import TextField from "@mui/material/TextField";
import {Grid} from '@material-ui/core';
import '../../../Css/Global/Global.css';

interface IVerificationCodeInput {
    setCode: Dispatch<SetStateAction<string[]>>;
    isLoadingVerify: boolean;
}

const VerificationCodeInput: React.FC<IVerificationCodeInput> = ({setCode: setCodeParams, isLoadingVerify}) => {
    const [code, setCode] = useState<string[]>(Array(6).fill(''));
    const inputRefs = Array(6).fill(null).map(() => React.createRef<HTMLInputElement>());


    const returnPreviousCharacter = (index: number) => {
        if (index > 0) {
            inputRefs[index - 1]?.current?.focus();
        }
    }

    const advanceNextCharacter = (index: number) => {
        if (index < inputRefs?.length - 1) {
            inputRefs[index + 1]?.current?.focus();
        }
    }

    const changeCode = (index: number, value: string) => (prevCode: string[]) => {
        if (index < 0 || index > (inputRefs?.length - 1)) {
            return prevCode;
        }

        const updatedCode = [...prevCode];
        updatedCode[index] = value;
        return updatedCode;
    }

    const handleKeyDown = (index: number) => (event: React.KeyboardEvent<HTMLInputElement>) => {
        const { key } = event;

        if (key === "Backspace") {
            returnPreviousCharacter(index);
            setCode(changeCode(index, ''));
        }

        if (key === "Delete") {
            advanceNextCharacter(index);
            setCode(changeCode(index + 1, ''));
        }

        if ((key === "ArrowLeft" || key === "ArrowDown") && index > 0) {
            returnPreviousCharacter(index);
        }

        if ((key === "ArrowRight" || key === "ArrowUp" ) && index < inputRefs?.length - 1) {
            advanceNextCharacter(index)
        }

        if ( /^[a-zA-Z0-9]$/.test(key)) {
            setCode(changeCode(index, key.toUpperCase()));
            advanceNextCharacter(index);
        }
    };

    const handlePaste = (event: ClipboardEvent) => {
        event.preventDefault();

        try {
            let pastedText = event.clipboardData?.getData('text/plain').trim() ?? '';

            if (/^[a-zA-Z0-9]{6}$/.test(pastedText)) {
                const newCode = pastedText.toUpperCase().split('');
                setCode(newCode);
            }
        } catch (error) {
            console.error("Erro ao processar o texto colado:", error);
        }
    };

    useEffect(() => {
        if(!isLoadingVerify) {
            setCode(Array(6).fill(''));
            inputRefs[0]?.current?.focus();
        }
    }, [isLoadingVerify]);

    useEffect(() => {
        if(code.filter(n => n).length === 6) {
            setCodeParams(code);
        }
    }, [code]);

    useEffect(() => {
        inputRefs.forEach(({current = null}) => {
            if(current !== null) {
                current?.addEventListener('paste', handlePaste);
            }
        })

        return () => {
            inputRefs.forEach(({current = null}) => {
                if(current !== null) {
                    current?.removeEventListener('paste', handlePaste);
                }
            })
        };
    }, []);

    return (
        <Grid container spacing={1} justifyContent="center">
            {code.map((digit, index) => (
                <Grid item xs={2} sm={1} md={1} key={index} style={{ display: 'flex', justifyContent: 'center' }}>
                    <TextField
                        value={code[index]}
                        onKeyDown={handleKeyDown(index)}
                        inputRef={inputRefs[index]}
                        inputProps={{ style: { textAlign: "center", width: '100%' } }}
                        style={{
                            width: "100%",
                            textTransform: 'uppercase',
                        }}
                        disabled={isLoadingVerify}
                    />
                </Grid>
            ))}
        </Grid>
    );
};

export default VerificationCodeInput;
